import './App.css';
import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import {
  useGridApiRef,
  DataGridPremium,
  GridActionsCellItem,
  GridRowModes,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
  GridToolbarFilterButton,
  svSE,
} from '@mui/x-data-grid-premium';

import moment from 'moment';
import { styled } from '@mui/material/styles';
import {
  Box,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';

/* ------------------------------------------------------------------ */
/* Styling                                                             */
/* ------------------------------------------------------------------ */

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export async function apiFetch(url, options = {}) {
  const res = await fetch(url, { credentials: 'include', ...options });
  if (res.status === 401) {
    const body = await res.json().catch(() => ({}));
    if (body.login_url) {
      window.location.href = body.login_url;
      return new Promise(() => {});
    }
  }
  return res;
}

function useParticipantsApi() {
  const getParticipants = async (formId, cache = 1) => {
    const res = await apiFetch(`/participants?form=${formId}&size=10000&cache=${cache}`, {
      headers: {
	    credentials: "include"
      },
    });
	
    return res.json();
  };

  const getQuestions = async (formId) => {
    const res = await apiFetch(`/questions?form_id=${formId}`, {
      headers: {
		credentials: "include"
      },
    });

    if (!res.ok) {
      throw new Error(`Failed to load questions (${res.status})`);
    }

    return res.json();
  };

  const updateStatus = async (memberNo, answers) => {
    await apiFetch(`/update_status?member_no=${memberNo}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(answers),
	  credentials: "include"
    });
  };

  return { getParticipants, getQuestions, updateStatus };
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function getQuestionType(type) {
  switch (type) {
    case 'choice':
      return 'singleSelect';
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    default:
      return 'string';
  }
}

function choiceFormatter({ value, field, api }) {
  const colDef = api.getColumn(field);
  const option = colDef?.valueOptions?.find((opt) => opt.value === value);
  return option ? option.label : value ?? '';
}

/**
 * Used for the detail panel only
 * We do not reuse valueGetter, because DataGrid passes extra parameters.
 */
function getDisplayValue(question, row) {
  const value = row?.questions?.[question.id];

  if (value == null) return '';

  if (question.type === 'other_unsupported_by_api' &&
      typeof value === 'string' &&
      value.includes('linked_id')
     ) {
    try {
      return JSON.parse(value)?.value ?? '';
    } catch {
      return '';
    }
  }

  if (question.type === 'boolean') return value ? 'Ja' : 'Nej';
  if (Array.isArray(value)) return value.join(', ');

  return String(value);
}

/* ------------------------------------------------------------------ */
/* Column builders (OBJECT-safe)                                       */
/* ------------------------------------------------------------------ */

function buildStatusColumns(questionsObject) {
  const questions = Object.values(questionsObject);

  return questions
    .filter((q) => q.status)
    .map((q) => {
      const column = {
        field: String(q.id),
        headerName: q.question,
        editable: true,
        type: getQuestionType(q.type),
        width: 11 * q.question.length,

        valueGetter: (params) => params?.row?.questions?.[q.id] ?? '',
        valueSetter: (params) => ({
          ...params.row,
          questions: {
            ...params.row.questions,
            [q.id]: params.value,
          },
        }),
      };

      if (q.type === 'choice') {
        column.valueOptions = Object.entries(q.choices).map(
          ([value, meta]) => ({
            value,
            label: meta.option,
          })
        );
        column.valueFormatter = choiceFormatter;
      }

      return column;
    });
}

function buildDataColumns(questionsObject) {
  const questions = Object.values(questionsObject);

  return questions
    .filter((q) => !q.status)
    .map((q) => ({
      field: String(q.id),
      headerName: q.question,
      tab: q.tab_title,
      section: q.section_title,
      type: q.type === 'choice' ? 'string' : getQuestionType(q.type),
      width: 10 * q.question.length,
      question: q, // store full question for detail panel

      valueGetter: (params) => {
        const value = params?.row?.questions?.[q.id];
        if (value == null) return '';
        // Multi-choice answers arrive as arrays.
        return Array.isArray(value) ? value.join(', ') : value;
      },
    }));
}

/* ------------------------------------------------------------------ */
/* Detail panel (safe, no DataGrid internals reused)                   */
/* ------------------------------------------------------------------ */

function DetailPanel({ row, dataColumns, baseColumns }) {
  let currentTab = '';
  let currentSection = '';

  return (
    <Box sx={{ padding: 2, maxWidth: '90vw' }}>
      <Table size="small" sx={{ width: '100%' }}>
        <TableBody>
          <StyledTableRow>
            <TableCell colSpan={2}><b>Grundläggande info</b></TableCell>
          </StyledTableRow>

          <StyledTableRow>
            <TableCell>Avanmäld</TableCell>
            <TableCell>{row.cancelled_date}</TableCell>
          </StyledTableRow>

          <StyledTableRow>
            <TableCell>E‑post</TableCell>
            <TableCell>{row.primary_email}</TableCell>
          </StyledTableRow>

          <StyledTableRow>
            <TableCell>Kön</TableCell>
            <TableCell>
              {
                baseColumns.find((c) => c.field === 'sex')?.valueOptions?.find((o) => o.value === row.sex)?.label
              }
            </TableCell>
          </StyledTableRow>

          {dataColumns.map((col) => {
            const q = col.question;
            const rows = [];

            if (q.tab_title && q.tab_title !== currentTab) {
              currentTab = q.tab_title;
              rows.push(
                <StyledTableRow key={`tab-${currentTab}`}>
                  <TableCell colSpan={2}>
                    <h3>{currentTab}</h3>
                  </TableCell>
                </StyledTableRow>
              );
            }

            if (q.section_title && q.section_title !== currentSection) {
              currentSection = q.section_title;
              rows.push(
                <StyledTableRow key={`section-${currentSection}`}>
                  <TableCell colSpan={2}>
                    <b>{currentSection}</b>
                  </TableCell>
                </StyledTableRow>
              );
            }

            rows.push(
              <StyledTableRow key={q.id}>
                <TableCell>{q.question}</TableCell>
                <TableCell>{getDisplayValue(q, row)}</TableCell>
              </StyledTableRow>
            );

            return rows;
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Toolbar                                                             */
/* ------------------------------------------------------------------ */

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
      <GridToolbarQuickFilter sx={{ marginLeft: 'auto' }} />
    </GridToolbarContainer>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

export default function Participants() {
  const apiRef = useGridApiRef();
  const params = useParams();
  const { getParticipants, getQuestions, updateStatus } =
    useParticipantsApi();

  const [rows, setRows] = useState([]);
  const [statusColumns, setStatusColumns] = useState([]);
  const [dataColumns, setDataColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState(null);
  const [rowModesModel, setRowModesModel] = useState({});

  /* -------------------- initial load -------------------------------- */

  useEffect(() => {
    async function load() {
      setLoading(true);
    
      const questions = await getQuestions(params.form_id);
      const participants = await getParticipants(params.form_id);
    
      setStatusColumns(buildStatusColumns(questions));
      setDataColumns(buildDataColumns(questions));
      setRows(participants.items);
    
      setLoading(false);
    }

    load();
  }, [params.form_id]);

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = useCallback(
    async (newRow, oldRow) => {
      const answers = {};
      statusColumns.forEach((c) => {
        answers[c.field] = newRow.questions[c.field];
      });

      await updateStatus(oldRow.member_no, answers);

      const refreshed = await getParticipants(params.form_id, 0);
      setRows(refreshed.items);

      setSnackbar({
        children: `Deltagare ${oldRow.member_no} uppdaterad`,
        severity: 'success',
      });

      return newRow;
    },
    [statusColumns, params.form_id]
  );

  /* -------------------- base + action columns ------------------------ */

  const baseColumns = [
    { field: 'member_no', headerName: 'Medlemsnummer', width: 150 },
    { field: 'first_name', headerName: 'Förnamn' },
    { field: 'last_name', headerName: 'Efternamn' },
    {
      field: 'date_of_birth',
      headerName: 'Födelsedatum',
      type: 'date',
      width: 180,
      valueGetter: ({ value }) => value && new Date(value),
      valueFormatter: (p) => moment(p.value).format('YYYY-MM-DD'),
    },
    {
      field: 'registration_date',
      headerName: 'Anmälningsdatum',
      type: 'dateTime',
      width: 180,
      valueGetter: ({ value }) => value && new Date(value),
      valueFormatter: (p) => moment(p.value).format('YYYY-MM-DD HH:mm'),
    },
    {
      field: 'sex',
      headerName: 'Kön',
      type: 'singleSelect',
      hide: true,
      valueOptions: [
        { value: 0, label: 'Okänt' },
        { value: 1, label: 'Man' },
        { value: 2, label: 'Kvinna' },
        { value: 3, label: 'Annat' },
      ],
      valueFormatter: choiceFormatter,
    },
  ];
  
  const [canCheckIn, setCanCheckIn] = useState(false);
  apiFetch("/info", { credentials: "include" })
    .then(res => res.json())
    .then(data => {
      setCanCheckIn(data.permissions.can_check_in);
    });

  const actionsColumn = {
    field: 'actions',
    type: 'actions',
    width: 100,
    getActions: ({ id }) => {
      const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;
  
      return isEditing
        ? [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
            />,
          ]
        : [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={handleEditClick(id)}
            />,
          ];
    },
  };

  return (
    <Box sx={{ height: '85vh', margin: 4 }}>
      <DataGridPremium
        apiRef={apiRef}
        rows={rows}
        columns={[
          ...baseColumns,
          ...statusColumns,
          ...dataColumns,
          ...(canCheckIn ? [actionsColumn] : []),
        ]}
        getRowId={(row) => row.member_no}
        loading={loading}
        density="compact"
        localeText={svSE.components.MuiDataGrid.defaultProps.localeText}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        processRowUpdate={processRowUpdate}
        components={{ Toolbar: CustomToolbar }}
        getDetailPanelContent={({ row }) => (
          <DetailPanel
            row={row}
            dataColumns={dataColumns}
            baseColumns={baseColumns}
          />
        )}
        getDetailPanelHeight={() => 'auto'}
      />

      {snackbar && (
        <Snackbar
          open
          autoHideDuration={6000}
          onClose={() => setSnackbar(null)}
        >
          <Alert {...snackbar} />
        </Snackbar>
      )}
    </Box>
  );
}